import React, { useState, useCallback, useMemo } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import { Box } from "@mui/material";
import {
  Link,
  NavLink,
  useNavigate,
  useLocation,
  useParams,
} from "react-router-dom";

import { useTheme } from "../../../providers/ThemeContext";
import { HeaderStyles } from "./HeaderStyles";
import { NavItem } from "../../../types/RandomGlobalTypes";
import MobileNavDrawer from "../../mobileDrawer/MobileNavDrawer";
import { useI18n } from "../../../languages/providers/I18nProvider";
import BrandMark from "../../brand/BrandMark";
import DropdownMenu from "../../dropdownmenu/DropdownMenu";
import AnimatedThemeToggle from "../../toggleButton/AnimatedThemeToggle";
import { useEid55Nav } from "../../../config/campaigns";
import { EID55_PATH } from "../../../pages/EidCollection55/eidProducts";
import LanguageSwitch from "../../toggleButton/LanguageSwitch";

type HeaderProps = {
  transparentBg?: boolean;
  variant?: "default" | "eid55";
};

const EID55_BG = "#F5ECD4";
const EID55_NAVY = "#2E3E5C";
const EID55_CORAL = "#C7645A";
const EID55_STRIPE = ["#B8293A", "#36844A", "#151A20", "#FFFFFF"];

const Header = ({ transparentBg, variant = "default" }: HeaderProps) => {
  const isEid = variant === "eid55";
  const showEidNav = useEid55Nav();
  const [open, setOpen] = useState(false);

  const { theme } = useTheme();
  const { t } = useI18n();
  const isMobile = useMediaQuery("(max-width: 900px)");

  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const openMenu = useCallback(() => setOpen(true), []);
  const closeMenu = useCallback(() => setOpen(false), []);

  const styles = HeaderStyles(theme);

  const lp = useCallback((path: string) => `/${lang}${path}`, [lang]);

  const switchLang = useCallback(
    (newLang: "en" | "ar") => {
      const newPath = location.pathname.replace(`/${lang}`, `/${newLang}`);
      navigate(newPath);
    },
    [lang, location.pathname, navigate],
  );

  const serviceLinks = useMemo(
    () => [
      { label: t("services:flags"), to: lp("/services/custom-flags") },
      { label: t("services:flagpoles"), to: lp("/services/flagpoles") },
      { label: t("services:banners"), to: lp("/services/banners") },
      { label: t("services:badges"), to: lp("/services/badges") },
      { label: t("services:sticker"), to: lp("/services/sticker") },
      { label: t("services:apparel"), to: lp("/services/apparel") },
      { label: t("services:lanyards"), to: lp("/services/lanyards") },
    ],
    [t, lp],
  );

  // Eid Al Etihad: a single link to the collection page (no dropdown).
  // Hidden by EID55_NAV_ENABLED or after EID55_END.
  const eidItem: NavItem | null = useMemo(() => {
    if (!showEidNav) return null;
    return {
      label: t("nav:eid55"),
      href: lp(EID55_PATH),
    };
  }, [showEidNav, t, lp]);

  const onEidPage = location.pathname.startsWith(lp(EID55_PATH));

  const navItems: NavItem[] = useMemo(
    () => [
      { label: t("nav:ourwork"), href: lp("/ourwork") },
      { label: t("nav:about"), href: lp("/about") },
      { label: t("nav:contact"), href: lp("/contact") },
    ],
    [t, lp],
  );

  const linkSx = {
    position: "relative" as const,
    textDecoration: "none",
    color: "inherit",
    opacity: 0.9,
    fontWeight: 500,
    fontFamily: "'Poppins', sans-serif",
    fontSize: "1rem",
    px: 2.5,
    py: 0.25,
    borderRadius: "0.6rem",
    transition: "color .2s ease, opacity .2s ease, background-color .2s ease",
    "&:hover": { opacity: 1 },
    "&::after": {
      content: '""',
      position: "absolute",
      left: 0,
      right: 0,
      bottom: -6,
      height: 2,
      background: isEid ? EID55_CORAL : "currentColor",
      transform: "scaleX(0)",
      transformOrigin: "left",
      transition: "transform .25s ease",
    },
    "&:hover::after": { transform: "scaleX(1)" },
    '&[aria-current="page"]': {
      color: "inherit",
      fontWeight: 600,
      backgroundColor: "transparent",
      "&::after": { transform: "scaleX(1)" },
    },
  };

  return (
    <>
      <header
        style={{
          ...styles.header,
          backgroundColor: transparentBg
            ? "transparent"
            : isEid
              ? EID55_BG
              : styles.header.backgroundColor,
          position: "relative",
          zIndex: 2,
          color: transparentBg
            ? "#fff"
            : isEid
              ? EID55_NAVY
              : styles.header.color,
        }}
      >
        <div style={styles.logoWrap}>
          <Link to={lp("")} style={{ textDecoration: "none" }}>
            <BrandMark
              size={40}
              color={transparentBg ? "#fff" : theme.text}
              forceLight={isEid}
            />
          </Link>
        </div>

        <div style={styles.rightWrap}>
          {!isMobile && (
            <>
              <ul style={{ ...styles.navList, overflow: "visible" }}>
                <li>
                  <Box component={NavLink} to={lp("")} end sx={linkSx}>
                    {t("nav:home")}
                  </Box>
                </li>

                <DropdownMenu
                  label={
                    <Box component={NavLink} to={lp("/services")} sx={linkSx}>
                      {t("nav:services")}
                    </Box>
                  }
                  items={serviceLinks}
                  themeVars={
                    {
                      "--menu-bg": theme.primary2,
                      "--menu-text": theme.hoverText,
                      "--menu-accent": theme.muted,
                    } as React.CSSProperties
                  }
                />

                {eidItem && (
                  <li>
                    <Box component={NavLink} to={eidItem.href} sx={linkSx}>
                      {eidItem.label}
                      <Box
                        component="sup"
                        sx={{
                          fontSize: "0.65em",
                          fontWeight: 600,
                          color: EID55_CORAL,
                          marginInlineStart: "3px",
                        }}
                      >
                        55
                      </Box>
                    </Box>
                  </li>
                )}

                {navItems.map(({ label, href }) => (
                  <li key={href}>
                    <Box
                      component={NavLink}
                      to={href}
                      end={onEidPage && href === lp("/ourwork")}
                      sx={linkSx}
                    >
                      {label}
                    </Box>
                  </li>
                ))}
              </ul>

              <LanguageSwitch
                color={transparentBg ? "#fff" : isEid ? EID55_NAVY : "inherit"}
                onSwitch={switchLang}
              />
              <AnimatedThemeToggle color={isEid ? EID55_NAVY : undefined} />
            </>
          )}

          {isMobile && (
            <>
              <LanguageSwitch
                color={transparentBg ? "#fff" : isEid ? EID55_NAVY : "inherit"}
                onSwitch={switchLang}
              />
              <AnimatedThemeToggle color={isEid ? EID55_NAVY : undefined} />
              <IconButton
                onClick={openMenu}
                aria-label="Open navigation menu"
                size="small"
                sx={{
                  borderRadius: "10px",
                  width: 52,
                  height: 52,
                  backgroundColor: "transparent",
                  color: "inherit",
                  "& .MuiSvgIcon-root": { fontSize: 28 },
                  "&:hover": {
                    backgroundColor: theme.background,
                    opacity: 0.9,
                  },
                }}
              >
                <MenuIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </div>
        {isEid && (
          <div
            data-eid55-stripe=""
            aria-hidden="true"
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: 4,
              display: "flex",
            }}
          >
            {EID55_STRIPE.map((c) => (
              <span
                key={c}
                style={{
                  flex: 1,
                  background: c,
                  ...(c === "#FFFFFF" && {
                    borderTop: "1px solid rgba(0,0,0,0.08)",
                  }),
                }}
              />
            ))}
          </div>
        )}
      </header>

      <MobileNavDrawer
        variant={variant}
        open={open}
        onClose={closeMenu}
        items={[
          { label: t("nav:home"), href: lp("") },
          { label: t("nav:services"), href: lp("/services") },
          ...(eidItem ? [eidItem] : []),
          ...navItems,
        ]}
      />
    </>
  );
};

export default Header;
